import { uiText } from '../i18n/uiText';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Filter, AlertTriangle, ShieldAlert, UserRound, ListChecks, ShieldCheck } from 'lucide-react';
import { getApiErrorMessage, opsApi } from '../api';
import { LoadingState } from '../components/common/UI';
import StatusBadge from '../components/ui/StatusBadge';
import PageIntro from '../components/ui/PageIntro';
import { safeInternalRoute } from '../utils/safeNavigation';
import RegistryPreferences, { useRegistrySettings, sortRegistryRows } from '../components/registry/RegistryPreferences';
import registryStyles from '../components/registry/RegistryWorkspace.module.css';
const REGISTRY_COLUMNS = [{key:'source',label:uiText("Источник")},{key:'audit',label:uiText("Аудит")},{key:'title',label:uiText("Описание"),required:true},{key:'owner',label:uiText("Исполнитель")},{key:'due',label:uiText("Срок")},{key:'status',label:uiText("Статус")},{key:'verification',label:uiText("Верификация")},{key:'escalation',label:uiText("Эскалация")},{key:'procedure',label:uiText("Маршрут закрытия")},{key:'actions',label:uiText("Действия"),required:true}];

const SOURCE_META = {
  issue: { label: uiText("Замечание"), cls: 'badge-gray' },
  corrective_plan: { label: uiText("ПКПД"), cls: 'badge-blue' },
};

const STATUS_META = {
  open: { label: uiText("Открыто (работы не начаты)"), cls: 'badge-red' },
  in_progress: { label: uiText("В работе (исполняется)"), cls: 'badge-blue' },
  fixed: { label: uiText("Исполнение заявлено (ждёт проверки)"), cls: 'badge-orange' },
  submitted: { label: uiText("Передано аудитору на проверку"), cls: 'badge-blue' },
  verification: { label: uiText("Проверяется аудитором"), cls: 'badge-blue' },
  on_verification: { label: uiText("Проверяется аудитором"), cls: 'badge-blue' },
  in_review: { label: uiText("Проверяется аудитором"), cls: 'badge-blue' },
  verified: { label: uiText("Подтверждено аудитором"), cls: 'badge-green' },
  resolved: { label: uiText("Устранено и подтверждено"), cls: 'badge-green' },
  closed: { label: uiText("Закрыто (пункт завершён)"), cls: 'badge-green' },
  rejected: { label: uiText("Отклонено аудитором"), cls: 'badge-red' },
  needs_revision: { label: uiText("Возвращено на доработку"), cls: 'badge-orange' },
  reopened: { label: uiText("Открыто повторно"), cls: 'badge-red' },
  awaiting_report: { label: uiText("Ожидается отчёт об исполнении"), cls: 'badge-orange' },
  partially_verified: { label: uiText("Подтверждено частично"), cls: 'badge-orange' },
  overdue: { label: uiText("Просрочено"), cls: 'badge-red' },
};

const VERIFICATION_META = {
  pending: uiText("Ожидает проверки аудитора"),
  partial: uiText("Подтверждено частично"),
  verified: uiText("Подтверждено аудитором"),
  rejected: uiText("Возвращено на доработку"),
};

const ESCALATION_LABELS = {
  none: uiText("Нет"),
  first_warning: uiText("1-е предупреждение"),
  second_warning: uiText("2-е предупреждение"),
  rights_limited: uiText("Ограничение прав"),
  suspension: uiText("Приостановление"),
  termination: uiText("Расторжение"),
};

function fmtDate(dt) {
  if (!dt) return '—';
  try {
    return new Date(dt).toLocaleDateString('ru-RU');
  } catch {
    return dt;
  }
}

function variantFromBadgeClass(className) {
  if (className?.includes('green')) return 'success';
  if (className?.includes('orange')) return 'warning';
  if (className?.includes('red')) return 'danger';
  if (className?.includes('blue')) return 'info';
  return 'neutral';
}

function downloadBlob(filename, blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function MiniBarList({ items, onClick, emptyText = uiText("Нет данных") }) {
  if (!items?.length) {
    return <div className="empty-state" style={{ minHeight: 120 }}>{emptyText}</div>;
  }
  const max = Math.max(...items.map((x) => x.count || 0), 1);

  return (
    <div className="control-mini-bar-list">
      {items.map((item, idx) => {
        const width = Math.max(8, Math.round(((item.count || 0) / max) * 100));
        const clickable = typeof onClick === 'function';
        const Row = clickable ? 'button' : 'div';
        return (
          <Row
            key={`${item.key || item.name}-${idx}`}
            type={clickable ? 'button' : undefined}
            className={`control-mini-bar-row ${clickable ? 'is-clickable' : ''}`}
            onClick={() => clickable && onClick(item)}
          >
            <div className="control-mini-bar-heading">
              <span className="control-mini-bar-label" title={item.name}>{item.name}</span>
              <strong className="control-mini-bar-count">{item.count}</strong>
            </div>
            <div className="control-mini-bar-track">
              <div
                className={`control-mini-bar-fill ${idx % 2 ? 'is-secondary' : ''}`}
                style={{ width: `${width}%` }}
              />
            </div>
          </Row>
        );
      })}
    </div>
  );
}

function AttentionList({ items, onOpen, emptyText }) {
  if (!items?.length) {
    return <div className="empty-state" style={{ minHeight: 120 }}>{emptyText}</div>;
  }
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {items.map((row) => (
        <div
          key={row.id || `${row.audit_id}-${row.audit_title}`}
          className="list-card"
          style={{ cursor: 'pointer' }}
          onClick={() => onOpen(row)}
        >
          <div className="list-card-title">{row.title || row.audit_title}</div>
          <div className="list-card-meta">
            {row.audit_title || '—'}
            {row.responsible ? ` · ${row.responsible}` : ''}
            {row.due_date ? uiText(" · срок {v0}", {v0: (fmtDate(row.due_date))}) : ''}
            {row.reasons?.length ? ` · ${row.reasons.join(', ')}` : ''}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function CorrectiveActionsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resyncing, setResyncing] = useState(false);
  const [payload, setPayload] = useState({
    summary: {},
    registry: [],
    charts: { source_bars: [], owner_bars: [], classification_bars: [], procedure_bars: [] },
    attention: { overdue: [], verification: [], escalation: [], blocked_closure: [] },
  });

  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [classificationFilter, setClassificationFilter] = useState('all');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [onlyOverdue, setOnlyOverdue] = useState(false);
  const [onlyVerification, setOnlyVerification] = useState(false);
  const { settings, setSettings, visible } = useRegistrySettings('corrective', REGISTRY_COLUMNS);

  const reload = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await opsApi.controlMonitor();
      setPayload(res.data || {});
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const resyncStates = async () => {
    setResyncing(true);
    setError('');
    try {
      await opsApi.controlMonitorResync();
      await reload();
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setResyncing(false);
    }
  };

  const registry = payload.registry || [];
  const summary = payload.summary || {};
  const charts = payload.charts || {};
  const attention = payload.attention || {};

  const ownerOptions = useMemo(() => {
    const uniq = new Set();
    registry.forEach((row) => {
      if (row.responsible && row.responsible !== 'Не назначен' && row.responsible !== 'Не указан') {
        uniq.add(row.responsible);
      }
    });
    return Array.from(uniq).sort((a, b) => a.localeCompare(b, 'ru'));
  }, [registry]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return registry.filter((row) => {
      const haystack = [
        row.title,
        row.description,
        row.audit_title,
        row.responsible,
        row.classification,
        row.status,
        row.verification_result,
        row.procedure_label,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const matchesSearch = !q || haystack.includes(q);
      const matchesSource = sourceFilter === 'all' || row.source === sourceFilter;
      const matchesOwner = ownerFilter === 'all' || row.responsible === ownerFilter;
      const matchesOverdue = !onlyOverdue || row.overdue;
      const matchesVerification =
        !onlyVerification ||
        (row.source === 'corrective_plan' && ['pending', 'partial', 'rejected'].includes(row.verification_result || ''));
      const matchesClass =
        classificationFilter === 'all' || String(row.classification || '').toUpperCase() === classificationFilter;

      let matchesStatus = true;
      if (statusFilter !== 'all') {
        if (statusFilter === 'closed') {
          matchesStatus = !!row.is_closed;
        } else {
          matchesStatus = row.status === statusFilter;
        }
      }

      return (
        matchesSearch &&
        matchesSource &&
        matchesOwner &&
        matchesOverdue &&
        matchesVerification &&
        matchesClass &&
        matchesStatus
      );
    });
  }, [
    registry,
    search,
    sourceFilter,
    ownerFilter,
    onlyOverdue,
    onlyVerification,
    classificationFilter,
    statusFilter,
  ]);

  const filteredSourceBars = useMemo(() => {
    const counts = { issue: 0, corrective_plan: 0 };
    filtered.forEach((row) => {
      counts[row.source] += 1;
    });
    return [
      { key: 'issue', name: uiText("Обычные замечания"), count: counts.issue },
      { key: 'corrective_plan', name: uiText("Пункты ПКПД"), count: counts.corrective_plan },
    ].filter((x) => x.count > 0);
  }, [filtered]);

  const filteredOwnerBars = useMemo(() => {
    const counts = new Map();
    filtered.forEach((row) => {
      counts.set(row.responsible || uiText("Не указан"), (counts.get(row.responsible || uiText("Не указан")) || 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([key, count]) => ({ key, name: key, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }, [filtered]);

  const filteredClassBars = useMemo(() => {
    const counts = new Map();
    filtered.forEach((row) => {
      const key = row.classification || '—';
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([key, count]) => ({ key, name: key, count }))
      .sort((a, b) => b.count - a.count);
  }, [filtered]);

  const openRow = (row) => {
    navigate(safeInternalRoute(row.open_url, '/audits'));
  };

  const exportXlsx = async () => {
    try {
      const res = await opsApi.controlMonitorXlsx();
      downloadBlob('control_monitor.xlsx', res.data);
    } catch (e) {
      setError(getApiErrorMessage(e));
    }
  };

  if (loading) {
    return <LoadingState message={uiText("Загружаем корректирующие действия…")} />;
  }

  return (
    <div className={`workspace-page ${registryStyles.workspace}`} data-density={settings.density}>
      <PageIntro
        icon={ListChecks}
        title={uiText("Мониторинг корректирующих действий")}
        description={uiText("Единый серверный реестр по замечаниям и ПКПД: сроки, доказательства, верификация, просрочки, эскалация и блокировки закрытия.")}
        aside={(
          <div className="page-intro-actions">
          <button type="button" className="btn btn-outline" onClick={resyncStates} disabled={resyncing}>
            {resyncing ? uiText("Пересчёт...") : uiText("Пересчитать статусы")}
          </button>
          <button type="button" className="btn btn-outline" onClick={exportXlsx}>
            <Download size={16} style={{ marginRight: 6 }} />{uiText("Экспорт XLSX")}</button>
          </div>
        )}
      />

      <RegistryPreferences scope="corrective" columns={REGISTRY_COLUMNS} filters={{search,sourceFilter,statusFilter,classificationFilter,ownerFilter,onlyOverdue,onlyVerification}} settings={settings} onSettings={setSettings} allowSort onApply={query => { setSearch(typeof query.search==='string'?query.search:''); setSourceFilter(query.sourceFilter||'all'); setStatusFilter(query.statusFilter||'all'); setClassificationFilter(query.classificationFilter||'all'); setOwnerFilter(query.ownerFilter||'all'); setOnlyOverdue(query.onlyOverdue===true); setOnlyVerification(query.onlyVerification===true); }} />
      {error ? (
        <div className="alert alert-error" style={{ marginBottom: 16 }}>
          {error}
        </div>
      ) : null}

      {(payload.recommended_actions || []).length ? (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">
            <div className="card-title">{uiText("Рекомендованные действия")}</div>
          </div>
          <div className="card-body" style={{ display: 'grid', gap: 12 }}>
            {payload.recommended_actions.map((item) => (
              <div
                key={item.code}
                className="list-card"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(safeInternalRoute(item.target_url, '/corrective-actions'))}
              >
                <div className="list-card-title">{item.title}</div>
                <div className="list-card-meta">{item.description}</div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{summary.total || 0}</div>
          <div className="stat-label">{uiText("Всего пунктов контроля")}</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: '#C0392B' }}>{summary.open || 0}</div>
          <div className="stat-label">{uiText("Открытые / в работе")}</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: '#c2410c' }}>{summary.overdue || 0}</div>
          <div className="stat-label">{uiText("Просроченные")}</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: '#7c3aed' }}>{summary.due_soon || 0}</div>
          <div className="stat-label">{uiText("Срок ≤ 7 дней")}</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: '#2563eb' }}>{summary.verification || 0}</div>
          <div className="stat-label">{uiText("На верификации")}</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: '#16a34a' }}>{summary.closed_on_time || 0}</div>
          <div className="stat-label">{uiText("Закрыто в срок")}</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: '#dc2626' }}>{summary.closed_late || 0}</div>
          <div className="stat-label">{uiText("Закрыто с просрочкой")}</div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body">
          <div className="ap-corrective-filters">
            <div className="form-group">
              <label className="form-label">{uiText("Поиск")}</label>
              <input
                className="form-control"
                placeholder={uiText("Аудит, описание, ответственный...")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label">{uiText("Источник")}</label>
              <select className="form-control" value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
                <option value="all">{uiText("Все")}</option>
                <option value="issue">{uiText("Замечания")}</option>
                <option value="corrective_plan">{uiText("ПКПД")}</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">{uiText("Статус")}</label>
              <select className="form-control" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="all">{uiText("Все")}</option>
                <option value="open">{uiText("Открыто")}</option>
                <option value="in_progress">{uiText("В работе")}</option>
                <option value="on_verification">{uiText("На верификации")}</option>
                <option value="overdue">{uiText("Просрочено")}</option>
                <option value="closed">{uiText("Закрыто")}</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">{uiText("Класс несоответствия")}</label>
              <select className="form-control" value={classificationFilter} onChange={(e) => setClassificationFilter(e.target.value)}>
                <option value="all">{uiText("Все")}</option>
                {/* Бага 15: убраны дубликаты SIGNIFICANT/MINOR/RECOMMENDATION (англ.).
                    В системе действует ОРД-таксономия КН/ЗН/НН + Рекомендация. */}
                <option value={uiText("КН")}>{uiText("КН — критическое")}</option>
                <option value={uiText("ЗН")}>{uiText("ЗН — значительное")}</option>
                <option value={uiText("НН")}>{uiText("НН — незначительное")}</option>
                <option value="recommendation">{uiText("Рекомендация")}</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">{uiText("Ответственный")}</label>
              <select className="form-control" value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)}>
                <option value="all">{uiText("Все")}</option>
                {ownerOptions.map((owner) => (
                  <option key={owner} value={owner}>{owner}</option>
                ))}
              </select>
            </div>

            <div className="ap-corrective-filter-toggles">
              <label className="checkbox-row">
                <input type="checkbox" checked={onlyOverdue} onChange={(e) => setOnlyOverdue(e.target.checked)} />{uiText("Только просроченные")}</label>
              <label className="checkbox-row">
                <input type="checkbox" checked={onlyVerification} onChange={(e) => setOnlyVerification(e.target.checked)} />{uiText("Только верификация")}</label>
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-grid" style={{ marginBottom: 16 }}>
        <div className="card">
          <div className="card-header">
            <div className="card-title">
              <ListChecks size={18} style={{ marginRight: 8 }} />{uiText("Источники")}</div>
          </div>
          <div className="card-body">
            <MiniBarList items={filteredSourceBars.length ? filteredSourceBars : charts.source_bars} emptyText={uiText("Нет данных по источникам")} />
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <div className="card-title">
              <UserRound size={18} style={{ marginRight: 8 }} />{uiText("Ответственные")}</div>
          </div>
          <div className="card-body">
            <MiniBarList items={filteredOwnerBars.length ? filteredOwnerBars : charts.owner_bars} emptyText={uiText("Нет распределения по ответственным")} />
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <div className="card-title">
              <Filter size={18} style={{ marginRight: 8 }} />{uiText("Классификация")}</div>
          </div>
          <div className="card-body">
            <MiniBarList items={filteredClassBars.length ? filteredClassBars : charts.classification_bars} emptyText={uiText("Нет данных по классам")} />
          </div>
        </div>
      </div>

      <div className="dashboard-grid" style={{ marginBottom: 16 }}>
        <div className="card">
          <div className="card-header">
            <div className="card-title">
              <AlertTriangle size={18} style={{ marginRight: 8 }} />{uiText("Просроченные пункты")}</div>
          </div>
          <div className="card-body">
            <AttentionList items={attention.overdue} onOpen={openRow} emptyText={uiText("Нет просроченных пунктов")} />
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <div className="card-title">
              <ShieldAlert size={18} style={{ marginRight: 8 }} />{uiText("На верификации")}</div>
          </div>
          <div className="card-body">
            <AttentionList items={attention.verification} onOpen={openRow} emptyText={uiText("Нет пунктов на верификации")} />
          </div>
        </div>
      </div>

      <div className="dashboard-grid" style={{ marginBottom: 16 }}>
        <div className="card">
          <div className="card-header">
            <div className="card-title">
              <ShieldCheck size={18} style={{ marginRight: 8 }} />{uiText("Блокировки закрытия ААС")}</div>
          </div>
          <div className="card-body">
            <AttentionList items={attention.blocked_closure} onOpen={openRow} emptyText={uiText("Нет блокировок закрытия")} />
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <div className="card-title">
              <AlertTriangle size={18} style={{ marginRight: 8 }} />{uiText("Эскалация")}</div>
          </div>
          <div className="card-body">
            <AttentionList items={attention.escalation} onOpen={openRow} emptyText={uiText("Нет пунктов в эскалации")} />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">{uiText("Единый реестр")}</div>
        </div>
        <div className="card-body table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th data-column="source" hidden={!visible('source')}>{uiText("Источник")}</th>
                <th data-column="audit" hidden={!visible('audit')}>{uiText("Аудит")}</th>
                <th>{uiText("Описание, заголовок и класс")}</th>
                <th data-column="owner" hidden={!visible('owner')}>{uiText("Ответственный / исполнитель")}</th>
                <th data-column="due" hidden={!visible('due')}>{uiText("Срок")}</th>
                <th data-column="status" hidden={!visible('status')}>{uiText("Статус")}</th>
                <th data-column="verification" hidden={!visible('verification')}>{uiText("Верификация")}</th>
                <th data-column="escalation" hidden={!visible('escalation')}>{uiText("Эскалация")}</th>
                <th data-column="procedure" hidden={!visible('procedure')} title={uiText("Кто контролирует исполнение и на каком этапе находится закрытие")}>{uiText("Маршрут закрытия")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length ? (
                sortRegistryRows(filtered,settings.sort).map((row) => {
                  const statusMeta = STATUS_META[row.status] || { label: row.status || '—', cls: 'badge-gray' };
                  return (
                    <tr
                      key={row.id}
                      data-clickable="true"
                      tabIndex={0}
                      onClick={() => openRow(row)}
                      onKeyDown={(event) => {
                        if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                          event.preventDefault();
                          openRow(row);
                        }
                      }}
                    >
                      <td data-column="source" hidden={!visible('source')}>
                        <span className={`badge ${SOURCE_META[row.source]?.cls || 'badge-gray'}`}>
                          {SOURCE_META[row.source]?.label || row.source}
                        </span>
                      </td>
                      <td data-column="audit" hidden={!visible('audit')}>{row.audit_title}</td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{row.title}</div>
                        {row.description && row.description !== row.title ? (
                          <div style={{ color: 'var(--premium-text-secondary)', fontSize: 12, marginTop: 3 }}>{row.description}</div>
                        ) : null}
                        {row.classification ? (
                          <div style={{ color: 'var(--premium-text-secondary)', fontSize: 12 }}>{row.classification_label || row.classification}</div>
                        ) : null}
                      </td>
                      <td data-column="owner" hidden={!visible('owner')}>{row.responsible}</td>
                      <td data-column="due" hidden={!visible('due')}>{fmtDate(row.due_date)}</td>
                      <td data-column="status" hidden={!visible('status')}>
                        <StatusBadge
                          status={row.status}
                          type="issue"
                          label={statusMeta.label}
                          variant={variantFromBadgeClass(statusMeta.cls)}
                        />
                        {row.overdue ? (
                          <div style={{ color: '#c2410c', fontSize: 12, marginTop: 4 }}>{uiText("Просрочено")}</div>
                        ) : null}
                        {row.due_soon && !row.overdue ? (
                          <div style={{ color: '#7c3aed', fontSize: 12, marginTop: 4 }}>{uiText("≤ 7 дней")}</div>
                        ) : null}
                      </td>
                      <td data-column="verification" hidden={!visible('verification')}>{VERIFICATION_META[row.verification_result] || row.verification_result || uiText("Не передано на проверку")}</td>
                      <td data-column="escalation" hidden={!visible('escalation')}>{ESCALATION_LABELS[row.escalation_stage] || row.escalation_stage || '—'}</td>
                      <td data-column="procedure" hidden={!visible('procedure')}>{row.procedure_label || '—'}</td>
                      <td onClick={(event) => event.stopPropagation()}>
                        <button type="button" className="btn btn-outline btn-sm" onClick={() => openRow(row)}>{uiText("Открыть")}</button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={10}>{uiText("Нет записей по выбранным фильтрам.")}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
